'use client';

import { useEffect, useRef } from 'react';

import { isDesktop } from 'react-device-detect';
import useIsBody from '@wconcept/wconcept-ui-renewal/hooks/useIsBody';

import {
  ANCHOR_HOLD,
  HEADER_HEIGHT,
  SELECTOR,
  TURN_THRESHOLD,
} from './constants';
import { PinParts, queryPinParts, sumHeight } from './dom';

/** 스크롤 밖에서 한 번 재서 들고 쓰는 값들 */
interface Metrics {
  /** 필터 바 */
  $pin: HTMLElement | null;
  /** 1+2+3뎁스를 합친 높이 */
  fullHeight: number;
  /** 접힐 때 올라가는 거리(마지막 뎁스 위의 행들) */
  foldHeight: number;
  /** 껍데기가 흐름에 세워둘 높이 */
  seatHeight: number;
  /** 접기 시작하는 seatTop 값 */
  foldFrom: number;
}

/** 마지막으로 DOM에 반영한 상태. 같은 값이면 다시 쓰지 않는다 */
interface Applied {
  fold: number;
  pinned: boolean | null;
}

/**
 * 탭 고정에 쓰는 높이들을 잰다. 레이아웃 전이면 null.
 * 뎁스 수나 화면 폭이 바뀔 때만 부른다.
 */
const measure = ({ $wrap, $seat, $inner }: PinParts): Metrics | null => {
  const rows = Array.from($inner.querySelectorAll<HTMLElement>(SELECTOR.row));
  const fullHeight = sumHeight(rows);
  // 아직 레이아웃 전이면 재지 않는다
  if (!fullHeight) return null;

  // 테두리 두께(소수점 포함). 고정됐을 때 세워둘 높이는 접혀도 줄이지 않는다
  const { borderTopWidth, borderBottomWidth } = getComputedStyle($seat);
  const border =
    (parseFloat(borderTopWidth) || 0) + (parseFloat(borderBottomWidth) || 0);
  const seatHeight = fullHeight + border;

  const $bar = $wrap.querySelector(SELECTOR.filterBar);
  const $pin = $bar?.querySelector<HTMLElement>(SELECTOR.pin) ?? null;

  // 필터 바 위에서 흐름에 남는 줄(퀵 필터)의 높이. 필터 바는 이만큼 더 내려간 뒤 붙는다
  const barRows = $bar ? Array.from($bar.children) : [];
  const lead = $pin ? sumHeight(barRows.slice(0, barRows.indexOf($pin))) : 0;

  // 접힌 뒤 헤더 아래 남는 마지막(가장 깊은) 뎁스의 높이
  const lastHeight = rows[rows.length - 1].getBoundingClientRect().height;

  return {
    $pin,
    fullHeight,
    foldHeight: fullHeight - lastHeight,
    seatHeight,
    // 필터 바가 접힌 위치까지 따라올 수 있게 되는 지점
    foldFrom: $pin
      ? HEADER_HEIGHT + lastHeight - seatHeight - lead
      : HEADER_HEIGHT,
  };
};

/**
 * 카테고리 탭 고정 (모바일 전용)
 *
 * 스크롤을 내려 탭이 헤더에 닿으면 고정하고, foldFrom을 지나면 마지막(가장 깊은)
 * 뎁스만 남기고 위 뎁스를 헤더 뒤로 접는다. 위로 올리면 다시 펼친다.
 * 여기서는 위치와 상태 클래스만 바꾸고, 그 사이 움직임은 CSS transition이 그린다.
 *
 * @param rowCount 현재 그려진 뎁스 행 수. 바뀌면 높이를 다시 잰다
 */
export function useDepthPin(rowCount: number): void {
  const { isBody } = useIsBody();

  // 스크롤 방향과, 현재 방향으로 가장 멀리 간 지점(반환점).
  // effect가 다시 붙어도 이어지도록 ref로 둔다
  const isScrollDownRef = useRef(true);
  const turnPointRef = useRef(0);

  useEffect(() => {
    if (isDesktop) return undefined;

    let metrics: Metrics | null = null;
    let measuredRows = -1;
    let measuredInner: HTMLElement | null = null;
    let applied: Applied = { fold: -1, pinned: null };
    // 껍데기 높이를 바꾼 직후, 브라우저가 스크롤을 보정하는 구간
    let anchorUntil = 0;
    let ticking = false;
    let lastWidth = window.innerWidth;

    /** 반환점에서 TURN_THRESHOLD 이상 역행하면 방향을 뒤집는다 */
    const readDirection = (scrollY: number): boolean => {
      const goingDown = isScrollDownRef.current;
      const turned = goingDown
        ? turnPointRef.current - scrollY
        : scrollY - turnPointRef.current;

      // 보정 스크롤 중이거나 같은 방향으로 더 갔으면 반환점만 옮긴다
      if (performance.now() < anchorUntil || turned < 0) {
        turnPointRef.current = scrollY;
      } else if (turned >= TURN_THRESHOLD) {
        isScrollDownRef.current = !goingDown;
        turnPointRef.current = scrollY;
      }

      return isScrollDownRef.current;
    };

    const handleScroll = () => {
      const parts = queryPinParts();
      if (!parts) return;

      const { $wrap, $seat, $inner } = parts;
      const isScrollDown = readDirection(window.scrollY);

      // 고정 판정. 흐름에 남는 껍데기의 화면상 윗선을 기준선으로 쓴다
      const seatTop = $seat.getBoundingClientRect().top;
      const isPinned = seatTop <= HEADER_HEIGHT;

      // 뎁스 수가 바뀌거나 탭·필터 바가 새 노드로 다시 그려지면 높이를 다시 재고 반영 상태도 초기화한다
      if (
        !metrics ||
        measuredRows !== $inner.childElementCount ||
        measuredInner !== $inner ||
        metrics.$pin?.isConnected === false
      ) {
        metrics = measure(parts);
        measuredRows = $inner.childElementCount;
        measuredInner = $inner;
        applied = { fold: -1, pinned: null };
        // 필터 바의 sticky 기준선은 펼친 높이로 고정한다. 접힘은 transform이 맡는다
        if (metrics) {
          $wrap.style.setProperty(
            '--brand-pin-top',
            `${HEADER_HEIGHT + metrics.fullHeight}px`
          );
        }
      }
      if (!metrics) return;

      // 접힘 판정. 필터 바가 따라올 수 있는 구간(isStuck)에서 내려가는 중이면 접는다
      const { $pin, foldHeight, seatHeight, foldFrom } = metrics;
      const isStuck = seatTop <= foldFrom;
      const fold = isScrollDown && isStuck ? foldHeight : 0;

      $wrap.classList.toggle('is-pinned', isPinned);
      // 접힘/펼침 transition은 이 클래스가 있는 동안만 걸린다
      $wrap.classList.toggle('is-stuck', isStuck);
      $wrap.classList.toggle('is-folded', fold > 0);

      // 탭과 필터 바를 접힌 만큼 함께 끌어올린다. 올라간 뎁스는 불투명한 헤더가 가린다
      if (applied.fold !== fold) {
        applied.fold = fold;
        const shift = fold ? `translateY(${-fold}px)` : '';
        $inner.style.transform = shift;
        if ($pin) $pin.style.transform = shift;
      }

      // 고정 중에는 흐름에서 빠진 탭 높이만큼 껍데기를 세워 둔다
      if (applied.pinned !== isPinned) {
        applied.pinned = isPinned;
        $seat.style.height = isPinned ? `${seatHeight}px` : '';
        // 이어서 오는 보정 스크롤은 방향 판정에서 제외한다
        anchorUntil = performance.now() + ANCHOR_HOLD;
        turnPointRef.current = window.scrollY;
      }
    };

    // 스크롤 이벤트를 프레임당 한 번으로 묶는다
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        handleScroll();
        ticking = false;
      });
    };

    // 폭이 바뀌면 높이를 다시 잰다. 폭이 같은 resize(iOS 주소창 접힘 등)는 무시한다
    const onResize = () => {
      if (window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;
      metrics = null;
      handleScroll();
    };

    handleScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, [isBody, rowCount]);
}
